import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import { set, cloneDeep, isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Resizable } from 'react-resizable';
import classNames from 'classnames';

import Environment from 'StorytellerEnvironment';
import { BlockComponent } from 'types';
import { updateBlockComponent } from 'store/reducers/StoryReducer';
import { getJQueryHeight, socrataVizInvalidateSizeHelper } from 'editor/renderers/SharedRenderingUtils';
import {
  getComponentRenderer,
  getResizableComponentProps,
} from 'lib/components/block-component-renderers/shared/BlockComponentRendererReactLiaison';
import { isCSSHeightMediaComponent } from './cssComponentLists';
import { getReactRenderer } from './ComponentRegistry';
import { getGlobalFilters, getParameterOverrides } from 'store/selectors/DataSourceSelectors';
import { selectors as storySelectors } from 'store/selectors/StorySelectors';
import type { StorytellerState } from 'store/StorytellerReduxState';

interface ComponentResizableProps {
  componentData: BlockComponent;
  blockId: string;
  componentIndex: number;
  editMode: boolean;
}

export const ComponentResizable = forwardRef<HTMLDivElement, ComponentResizableProps>(
  ({ componentData, blockId, componentIndex, editMode }, ref) => {
    const componentRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const jQueryRendererToRun = getComponentRenderer(componentData.type);
    const jQueryResizableProps = useMemo(
      () => getResizableComponentProps(jQueryRendererToRun, componentData),
      // returns a new object every time, so we need to memoize it for the invalidate size useEffect
      [jQueryRendererToRun, componentData],
    );

    // Get React renderer if one exists for this component type
    const ReactRenderer = getReactRenderer(componentData.type);
    const theme = useSelector((state: StorytellerState) =>
      storySelectors.getStoryTheme(state, Environment.STORY_UID!),
    );
    const additionalFilters = useSelector(getGlobalFilters, isEqual);
    const parameterOverrides = useSelector(getParameterOverrides, isEqual);

    const reactRendererProps = useMemo(
      () => ({
        componentData,
        theme,
        editMode,
        blockId,
        componentIndex,
        additionalFilters,
        parameterOverrides,
      }),
      [componentData, theme, editMode, blockId, componentIndex, additionalFilters, parameterOverrides],
    );

    // Explicitly no dep array
    useImperativeHandle(ref, () => componentRef.current!);

    const fallbackHeight = jQueryResizableProps.defaultHeight || null;

    const [height, setHeight] = useState<number | null>(
      componentData.value?.layout?.height || fallbackHeight,
    );
    // If the component starts with any kind of height, we'll never need the jQuery switchover behavior.
    const [shouldUseJQueryHeight, setShouldUseJQueryHeight] = useState(!height);

    // Send SOCRATA_VISUALIZATION_INVALIDATE_SIZE event when height changes, with jQuery helper
    useEffect(() => {
      if (jQueryResizableProps.resizeSupported && componentRef?.current) {
        socrataVizInvalidateSizeHelper(componentRef.current);
      }
    }, [jQueryResizableProps.resizeSupported, height]);

    const onResize = (event: React.SyntheticEvent, { size }: { size: { width: number; height: number } }) => {
      const resizeHeight = shouldUseJQueryHeight
        ? getJQueryHeight(size.height, blockId, componentIndex, setShouldUseJQueryHeight)
        : size.height;

      setHeight(resizeHeight);

      // Update redux store
      const updatedComponentData = cloneDeep(componentData);
      set(updatedComponentData, 'value.layout.height', resizeHeight);

      dispatch(
        updateBlockComponent({
          blockId: blockId,
          componentIndex: componentIndex,
          type: updatedComponentData.type,
          value: updatedComponentData.value,
        }),
      );
    };

    // Ignore component height if resize is not supported, occurs when switching from measure chart to card.
    const finalHeight = jQueryResizableProps.resizeSupported ? height : fallbackHeight;

    const heightStyle = height ? { height: `${finalHeight}px` } : undefined;
    // I think assetSelectors are the only components which never need any kind of style.
    const classes = classNames(
      { 'react-resizable-style': !!height },
      { 'react-resizable-padding': jQueryResizableProps.resizeSupported },
    );

    const renderInnerDiv = () => {
      return (
        <div className={classes} data-testid="component-resizable-inner-div">
          <div
            className={classNames('component-container', {
              'legacy-css-height-media-component': isCSSHeightMediaComponent(componentData.type),
            })}
            ref={componentRef}
            data-component-index={componentIndex}
            style={heightStyle}
            {...(!editMode && componentData?.componentUuid
              ? { 'data-component-uuid': componentData?.componentUuid }
              : {})}
          >
            {ReactRenderer && <ReactRenderer {...reactRendererProps} />}
          </div>
        </div>
      );
    };

    if (!editMode || !jQueryResizableProps.resizeSupported) {
      return renderInnerDiv();
    }

    return (
      <Resizable
        // lie to react-resizable about the height if we're still using jQuery height
        height={height || 1}
        width={100} // Width doesn't matter since we're not resizing horizontally
        minConstraints={[100, jQueryResizableProps.resizeOptions?.minHeight || 0]}
        axis="y" // Only allow vertical resizing
        onResize={onResize}
        resizeHandles={['s']}
        handle={
          <div className="component-resize-handle">
            <div></div>
          </div>
        }
      >
        {renderInnerDiv()}
      </Resizable>
    );
  },
);
